/**
 * The displacement ceremony — "if this comes on, what comes off?" (D2).
 *
 * PRD: docs/9-exploration/DISPLACEMENT-AND-COMMITMENT-STATES-PRD.md §5.
 * RPC: supabase/migrations/701_displacement_ceremony.sql (one transaction).
 *
 * What this surface will not do, on purpose (PRD non-goals + L2):
 *  - It does not choose a victim. Candidates are listed in pool order; nothing
 *    is preselected, ranked by "least valuable", or recommended.
 *  - `accept_overcommitment` is a real, allowed answer sitting alongside the
 *    others — never a default, never hidden, and never anonymous: it takes a
 *    stated reason and the record names who accepted.
 *  - Numbers the record can compute (reduce, cancel) are shown as computed;
 *    numbers only a person can promise (delay, add resources) are labelled as
 *    stated. The tally uses the same rule the RPC does.
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Checkbox, Modal, Select, TextInput } from '../../../components/ds';
import {
  DISPOSITION_HELP,
  DISPOSITION_LABELS,
  admitWithDisplacement,
  selectionHoursFreed,
  type DisplacementDisposition,
  type DisplacementSelectionInput,
} from '../../../lib/displacementService';
import { listClaimsForCycle, type PlanResourceClaimRow } from '../../../lib/resourceClaimsService';
import type { AdmissionImpact } from '../../../lib/poolPressure';
import type { ResourceRequirementLine } from '../../../lib/commitmentState';
import { formatHours } from '../../../lib/commitmentState';

export interface DisplacementCandidate {
  itemId: string;
  name: string;
  /** Pools this commitment draws on — used to order candidates by relevance. */
  resourceTypes: readonly string[];
}

export default function DisplacementCeremonyModal({
  planCycleId,
  admittedItemId,
  admittedName,
  marginalRequirement,
  impact,
  candidates,
  onClose,
  onRecorded,
}: {
  planCycleId: string;
  admittedItemId: string;
  admittedName: string;
  marginalRequirement: readonly ResourceRequirementLine[];
  impact: AdmissionImpact;
  candidates: readonly DisplacementCandidate[];
  onClose: () => void;
  onRecorded: () => void;
}) {
  const [claims, setClaims] = useState<PlanResourceClaimRow[]>([]);
  const [selections, setSelections] = useState<Record<string, DisplacementSelectionInput>>({});
  const [accept, setAccept] = useState(false);
  const [acceptBasis, setAcceptBasis] = useState('');
  const [basis, setBasis] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void listClaimsForCycle(planCycleId).then(({ rows }) => {
      if (!cancelled) setClaims(rows);
    });
    return () => { cancelled = true; };
  }, [planCycleId]);

  const liveClaimHours = useMemo(() => {
    const byClaimId = new Map<string, number>();
    const byItemId = new Map<string, number>();
    for (const c of claims) {
      if (c.status !== 'declared' || c.claim_kind !== 'capacity_hours') continue;
      const hours = Number(c.declared_hours ?? 0);
      byClaimId.set(c.id, hours);
      byItemId.set(c.item_id, (byItemId.get(c.item_id) ?? 0) + hours);
    }
    return { byClaimId, byItemId };
  }, [claims]);

  const claimsByItem = useMemo(() => {
    const map = new Map<string, PlanResourceClaimRow[]>();
    for (const c of claims) {
      if (c.status !== 'declared' || c.claim_kind !== 'capacity_hours') continue;
      map.set(c.item_id, [...(map.get(c.item_id) ?? []), c]);
    }
    return map;
  }, [claims]);

  const chosen = Object.values(selections);
  const resolved = chosen.reduce((t, s) => t + selectionHoursFreed(s, liveClaimHours), 0);
  const remaining = Math.max(0, impact.shortfallHours - resolved);
  const answered = remaining <= 0 || (accept && acceptBasis.trim().length > 0);

  // Pools the admission actually pushes over, in shortfall order.
  const overPools = impact.pools.filter((p) => p.shortfallHours > 0);
  const overPoolTypes = new Set(overPools.map((p) => p.resourceType));

  // Candidates relevant to those pools come first; the rest stay available,
  // because a room may choose to give way somewhere the arithmetic did not ask.
  const orderedCandidates = useMemo(
    () =>
      [...candidates]
        .filter((c) => c.itemId !== admittedItemId)
        .sort((a, b) => {
          const aRel = a.resourceTypes.some((t) => overPoolTypes.has(t)) ? 0 : 1;
          const bRel = b.resourceTypes.some((t) => overPoolTypes.has(t)) ? 0 : 1;
          return aRel - bRel || a.name.localeCompare(b.name);
        }),
    // overPoolTypes is derived from impact; keying on impact keeps it stable.
    [candidates, admittedItemId, impact], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const setDisposition = (itemId: string, disposition: DisplacementDisposition | '') => {
    setSelections((prev) => {
      const next = { ...prev };
      if (!disposition) {
        delete next[itemId];
        return next;
      }
      const existing = next[itemId];
      const claimsHere = claimsByItem.get(itemId) ?? [];
      next[itemId] = {
        displacedItemId: itemId,
        disposition,
        resourceType: existing?.resourceType ?? claimsHere[0]?.resource_type ?? null,
        basis: existing?.basis ?? '',
        supersedeClaimId: disposition === 'reduce' ? (existing?.supersedeClaimId ?? claimsHere[0]?.id) : undefined,
        replacementHours: disposition === 'reduce' ? existing?.replacementHours : undefined,
        hoursFreed: disposition === 'delay' || disposition === 'add_resources' ? existing?.hoursFreed : undefined,
      };
      return next;
    });
  };

  const patch = (itemId: string, fields: Partial<DisplacementSelectionInput>) =>
    setSelections((prev) =>
      prev[itemId] ? { ...prev, [itemId]: { ...prev[itemId], ...fields } } : prev
    );

  const submit = async () => {
    setBusy(true);
    setError(null);
    const result = await admitWithDisplacement({
      planCycleId,
      admittedItemId,
      marginalRequirement,
      impact,
      selections: chosen,
      acceptOvercommitment: accept,
      acceptBasis: accept ? acceptBasis.trim() : undefined,
      basis: basis.trim() || undefined,
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'The ceremony could not be recorded.');
      return;
    }
    onRecorded();
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      label="If this comes on, what comes off?"
      title={admittedName}
      description={impact.sentence}
      footer={
        <>
          <span className="mr-auto text-sm tabular-nums text-[var(--text-sec)]">
            {formatHours(resolved)} of {formatHours(impact.shortfallHours)} answered
            {remaining > 0 && !accept && (
              <span className="text-[var(--coral)]"> · {formatHours(remaining)} still open</span>
            )}
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="border border-[var(--rule)] px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || !answered}
            className="flex items-center gap-2 bg-primary-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Record this
          </button>
        </>
      }
    >
      <div className="space-y-5">
          {/* ── What the pools say ─────────────────────────────────────── */}
          <section>
            <h3 className="text-[11px] font-mono uppercase tracking-wider text-[var(--text-ter)]">
              What the pools say
            </h3>
            <ul className="mt-2 space-y-1.5">
              {impact.pools.map((p) => (
                <li key={p.resourceType} className="text-sm text-[var(--text-sec)]">
                  <span className="capitalize text-[var(--text-pri)]">{p.resourceType}</span>
                  {' — '}
                  {p.noCapacityOnFile ? (
                    <span className="text-[var(--text-ter)]">
                      no capacity on file, so this pool is unmeasured
                    </span>
                  ) : (
                    <>
                      {formatHours(p.requiredBeforeHours)} committed + {formatHours(p.marginalHours)} new
                      {' vs '}
                      {formatHours(p.thresholdHours)} available
                      {p.shortfallHours > 0 && (
                        <span className="text-[var(--coral)]">
                          {' '}· over by {formatHours(p.shortfallHours)}
                        </span>
                      )}
                      {p.alreadyOver && (
                        <span className="text-[var(--text-ter)]"> · already over before this</span>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          </section>

          {/* ── The choices ────────────────────────────────────────────── */}
          <section>
            <h3 className="text-[11px] font-mono uppercase tracking-wider text-[var(--text-ter)]">
              What gives way
            </h3>
            <p className="mt-1 text-xs text-[var(--text-ter)]">
              Nothing is preselected. Choosing nothing and accepting the overcommitment below is a
              real answer — it is simply recorded as one.
            </p>
            <div className="mt-3 divide-y divide-[var(--rule)] border border-[var(--rule)]">
              {orderedCandidates.length === 0 && (
                <p className="px-3 py-4 text-sm text-[var(--text-ter)]">
                  No other commitments are on this cycle's record to give way.
                </p>
              )}
              {orderedCandidates.map((c) => {
                const sel = selections[c.itemId];
                const itemClaims = claimsByItem.get(c.itemId) ?? [];
                const freed = sel ? selectionHoursFreed(sel, liveClaimHours) : 0;
                return (
                  <div key={c.itemId} className="px-3 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="flex-1 min-w-0 truncate text-sm text-[var(--text-pri)]">
                        {c.name}
                      </span>
                      {itemClaims.length > 0 && (
                        <span className="text-xs text-[var(--text-ter)] tabular-nums">
                          {formatHours(liveClaimHours.byItemId.get(c.itemId) ?? 0)} claimed
                        </span>
                      )}
                      <Select
                        size="sm"
                        aria-label={`What happens to ${c.name}`}
                        value={sel?.disposition ?? ''}
                        onChange={(e) =>
                          setDisposition(c.itemId, e.target.value as DisplacementDisposition | '')
                        }
                        options={[
                          { value: '', label: 'Leave it alone' },
                          ...(Object.keys(DISPOSITION_LABELS) as DisplacementDisposition[]).map(
                            (d) => ({ value: d, label: DISPOSITION_LABELS[d] }),
                          ),
                        ]}
                      />
                    </div>

                    {sel && (
                      <div className="mt-2 space-y-2 border-l-2 border-[var(--rule)] pl-3">
                        <p className="text-xs text-[var(--text-ter)]">
                          {DISPOSITION_HELP[sel.disposition]}
                        </p>

                        {sel.disposition === 'reduce' && (
                          <div className="flex flex-wrap items-center gap-2">
                            <Select
                              size="sm"
                              aria-label="Which claim to reduce"
                              value={sel.supersedeClaimId ?? ''}
                              onChange={(e) => patch(c.itemId, { supersedeClaimId: e.target.value })}
                              options={
                                itemClaims.length === 0
                                  ? [{ value: '', label: 'No claim to reduce' }]
                                  : itemClaims.map((cl) => ({
                                      value: cl.id,
                                      label: `${cl.resource_type ?? 'unattributed'} · ${formatHours(Number(cl.declared_hours ?? 0))}`,
                                    }))
                              }
                            />
                            <label className="text-xs text-[var(--text-ter)]">
                              leaves
                              <TextInput
                                type="number"
                                size="sm"
                                min={0}
                                value={sel.replacementHours ?? ''}
                                onChange={(e) =>
                                  patch(c.itemId, {
                                    replacementHours:
                                      e.target.value === '' ? undefined : Number(e.target.value),
                                  })
                                }
                                className="ml-1 w-20"
                              />
                              <span className="ml-1">hours</span>
                            </label>
                          </div>
                        )}

                        {(sel.disposition === 'delay' || sel.disposition === 'add_resources') && (
                          <label className="block text-xs text-[var(--text-ter)]">
                            Hours this frees, as you estimate them
                            <TextInput
                              type="number"
                              size="sm"
                              min={0}
                              value={sel.hoursFreed ?? ''}
                              onChange={(e) =>
                                patch(c.itemId, {
                                  hoursFreed: e.target.value === '' ? undefined : Number(e.target.value),
                                })
                              }
                              className="ml-2 w-24"
                            />
                          </label>
                        )}

                        <TextInput
                          size="sm"
                          value={sel.basis ?? ''}
                          onChange={(e) => patch(c.itemId, { basis: e.target.value })}
                          placeholder="Why this one gives way"
                          aria-label={`Why ${c.name} gives way`}
                        />
                        <p className="text-xs text-[var(--text-ter)]">
                          Frees {formatHours(freed)}
                          {sel.disposition === 'reduce' || sel.disposition === 'cancel'
                            ? ' (computed from the claims)'
                            : ' (your estimate)'}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── Overcommitment: allowed, recorded, named ───────────────── */}
          {remaining > 0 && (
            <section className="border border-[var(--mustard)] bg-[var(--mustard-dim)] p-3">
              <Checkbox
                checked={accept}
                onChange={(e) => setAccept(e.target.checked)}
                label={`Accept the overcommitment — ${formatHours(remaining)} still unanswered`}
                description="This is allowed. The record will show that you accepted it, and why."
              />
              {accept && (
                <TextInput
                  size="sm"
                  value={acceptBasis}
                  onChange={(e) => setAcceptBasis(e.target.value)}
                  placeholder="Why the overcommitment is being accepted"
                  aria-label="Why the overcommitment is being accepted"
                  className="mt-2"
                />
              )}
            </section>
          )}

          <section>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-[var(--text-ter)]">
              Why this is being admitted
            </label>
            <TextInput
              size="sm"
              value={basis}
              onChange={(e) => setBasis(e.target.value)}
              placeholder="The reason this rose above the line"
              aria-label="Why this is being admitted"
              className="mt-1.5"
            />
          </section>

        {error && (
          <p className="border border-[var(--coral)] bg-[var(--coral-dim)] p-2.5 text-sm text-[var(--coral)]">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
